import { z } from 'zod';

export const vitalSignsNippleDtoSchema = z.object({
  appearance: z.string().nullable(),
  breastfeeding: z.array(z.string()).nullable(),
  appearanceI18n: z.string().nullable(),
  breastfeedingI18n: z.array(z.string()).nullable()
});
export type VitalSignsNippleDto = z.infer<typeof vitalSignsNippleDtoSchema>;

export const vitalSignsWoundDtoSchema = z.object({
  type: z.string().nullable(),
  irrigation: z.string().nullable(),
  condition: z.array(z.string()).nullable(),
  typeI18n: z.string().nullable(),
  irrigationI18n: z.string().nullable(),
  conditionI18n: z.array(z.string()).nullable()
});
export type VitalSignsWoundDto = z.infer<typeof vitalSignsWoundDtoSchema>;

export const vitalSignsLochiaDtoSchema = z.object({
  amount: z.string().nullable(),
  color: z.string().nullable(),
  odor: z.string().nullable(),
  amountI18n: z.string().nullable(),
  colorI18n: z.string().nullable(),
  odorI18n: z.string().nullable()
});
export type VitalSignsLochiaDto = z.infer<typeof vitalSignsLochiaDtoSchema>;

export const vitalSignsUterineContractionDtoSchema = z.object({
  intensity: z.string().nullable(),
  fundalHeight: z.string().nullable(),
  intensityI18n: z.string().nullable(),
  fundalHeightI18n: z.string().nullable()
});
export type VitalSignsUterineContractionDto = z.infer<typeof vitalSignsUterineContractionDtoSchema>;

export const intakeOutputAspectDtoSchema = z.object({
  code: z.string().nullable(),
  displayName: z.string().nullable()
});
export type IntakeOutputAspectDto = z.infer<typeof intakeOutputAspectDtoSchema>;

export const alertMessageDtoSchema = z.object({
  valueQuantity: z.string().nullable(),
  valueUnit: z.string().nullable(),
  itemNameEn: z.string().nullable(),
  itemNameZh: z.string().nullable(),
  resultStatus: z.string().nullable()
});
export type AlertMessageDto = z.infer<typeof alertMessageDtoSchema>;

export const dailyCareIoItemDtoSchema = z.object({
  observationCode: z.string().nullable(),
  displayName: z.string().nullable(),
  valueUnit: z.string().nullable(),
  total: z.number().nullable()
});
export type DailyCareIoItemDto = z.infer<typeof dailyCareIoItemDtoSchema>;

export const dailyCareShiftStatusDtoSchema = z.object({
  shiftPeriod: z.enum(["NIGHT", "DAY", "EVENING"]).nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable(),
  batchId: z.string().nullable()
});
export type DailyCareShiftStatusDto = z.infer<typeof dailyCareShiftStatusDtoSchema>;

export const vitalSignsObstetricRecordDtoSchema = z.object({
  uterineContraction: vitalSignsUterineContractionDtoSchema.nullable(),
  lochia: vitalSignsLochiaDtoSchema.nullable(),
  wound: vitalSignsWoundDtoSchema.nullable(),
  nipple: vitalSignsNippleDtoSchema.nullable()
});
export type VitalSignsObstetricRecordDto = z.infer<typeof vitalSignsObstetricRecordDtoSchema>;

export const dailyCareVitalSignItemDtoSchema = z.object({
  observationCode: z.string().nullable(),
  valueUnit: z.string().nullable(),
  valueQuantity: z.number().nullable(),
  hour: z.string().nullable(),
  effectiveDatetime: z.string().nullable()
});
export type DailyCareVitalSignItemDto = z.infer<typeof dailyCareVitalSignItemDtoSchema>;

export const dailyCareVitalSlotDtoSchema = z.object({
  batchId: z.string().nullable(),
  hour: z.string().nullable(),
  scheduledDatetime: z.string().nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable(),
  effectiveDatetime: z.string().nullable()
});
export type DailyCareVitalSlotDto = z.infer<typeof dailyCareVitalSlotDtoSchema>;

export const dailyCareScheduledDoseDtoSchema = z.object({
  administrationId: z.string().nullable(),
  scheduledDatetime: z.string().nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable(),
  effectiveDatetime: z.string().nullable()
});
export type DailyCareScheduledDoseDto = z.infer<typeof dailyCareScheduledDoseDtoSchema>;

export const dailyCareTimelineItemDtoSchema = z.object({
  itemType: z.enum(["MEDICATION", "MEDICATION_INJECTION", "LAB", "BODY_DATA", "NURSING_ORDER", "EXAM", "SURGERY", "VITAL_SIGN", "IO_NIGHT", "IO_DAY", "IO_EVENING"]).nullable(),
  refId: z.string().nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable()
});
export type DailyCareTimelineItemDto = z.infer<typeof dailyCareTimelineItemDtoSchema>;

export const intakeOutputCellDtoSchema = z.object({
  valueQuantity: z.number().nullable(),
  aspect: intakeOutputAspectDtoSchema.nullable(),
  note: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type IntakeOutputCellDto = z.infer<typeof intakeOutputCellDtoSchema>;

export const transferDtoSchema = z.object({
  status: z.enum(["TRANSFERRING_IN", "TRANSFERRING_OUT"]).nullable(),
  transferDatetime: z.string().nullable(),
  toBedNo: z.string().nullable()
});
export type TransferDto = z.infer<typeof transferDtoSchema>;

export const alertDtoSchema = z.object({
  id: z.string().nullable(),
  userId: z.string().nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  bedNo: z.string().nullable(),
  createDatetime: z.string().nullable(),
  message: alertMessageDtoSchema.nullable()
});
export type AlertDto = z.infer<typeof alertDtoSchema>;

export const tenantCodeBaseDtoSchema = z.object({
  id: z.string().nullable(),
  codeType: z.string().nullable(),
  code: z.string().nullable(),
  description: z.string().nullable()
});
export type TenantCodeBaseDto = z.infer<typeof tenantCodeBaseDtoSchema>;

export const vitalSignsAlertValueDtoSchema = z.object({
  value: z.number().nullable(),
  abnormalFlag: z.boolean().nullable()
});
export type VitalSignsAlertValueDto = z.infer<typeof vitalSignsAlertValueDtoSchema>;

export const dailyCareIntakeOutputSummaryDtoSchema = z.object({
  shiftStatuses: z.array(dailyCareShiftStatusDtoSchema).nullable(),
  intake: z.array(dailyCareIoItemDtoSchema).nullable(),
  output: z.array(dailyCareIoItemDtoSchema).nullable()
});
export type DailyCareIntakeOutputSummaryDto = z.infer<typeof dailyCareIntakeOutputSummaryDtoSchema>;

export const dailyCareTaskBoardItemDtoSchema = z.object({
  orderId: z.string().nullable(),
  orderCategory: z.enum(["MED", "EXAM", "LAB", "BODY_DATA", "NURSING_ORDER", "SURGERY", "VITAL_SIGN", "INTAKE_OUTPUT"]).nullable(),
  orderName: z.string().nullable(),
  locationName: z.string().nullable(),
  scheduledDatetime: z.string().nullable(),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]).nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable()
});
export type DailyCareTaskBoardItemDto = z.infer<typeof dailyCareTaskBoardItemDtoSchema>;

export const dailyCareVitalSignsDtoSchema = z.object({
  vitalSlots: z.array(dailyCareVitalSlotDtoSchema).nullable(),
  items: z.array(dailyCareVitalSignItemDtoSchema).nullable(),
  obstetricRecords: vitalSignsObstetricRecordDtoSchema.nullable()
});
export type DailyCareVitalSignsDto = z.infer<typeof dailyCareVitalSignsDtoSchema>;

export const dailyCareMedicationDtoSchema = z.object({
  orderId: z.string().nullable(),
  orderCode: z.string().nullable(),
  orderName: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  prnFlag: z.boolean().nullable(),
  injectionFlag: z.boolean().nullable(),
  instructionMemo: z.string().nullable(),
  stopFlag: z.boolean().nullable(),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]).nullable(),
  scheduledDoses: z.array(dailyCareScheduledDoseDtoSchema).nullable(),
  prnNextDatetime: z.string().nullable(),
  mixItemNameList: z.array(z.string()).nullable()
});
export type DailyCareMedicationDto = z.infer<typeof dailyCareMedicationDtoSchema>;

export const dailyCareTimelineGroupDtoSchema = z.object({
  datetime: z.string().nullable(),
  items: z.array(dailyCareTimelineItemDtoSchema).nullable()
});
export type DailyCareTimelineGroupDto = z.infer<typeof dailyCareTimelineGroupDtoSchema>;

export const dailyCarePatientTodoDtoSchema = z.object({
  id: z.string().nullable(),
  type: z.enum(["BED_TRANSFER", "REPORT", "MEMO"]).nullable(),
  content: z.string().nullable(),
  subLabel: z.string().nullable(),
  dueDatetime: z.string().nullable(),
  pinFlag: z.boolean().nullable(),
  completeFlag: z.boolean().nullable(),
  transferStatus: z.enum(["TRANSFERRING_IN", "TRANSFERRING_OUT"]).nullable()
});
export type DailyCarePatientTodoDto = z.infer<typeof dailyCarePatientTodoDtoSchema>;

export const dailyCareShiftFormTodoDtoSchema = z.object({
  formTodoId: z.string().nullable(),
  templateName: z.string().nullable(),
  effectiveDatetime: z.string().nullable()
});
export type DailyCareShiftFormTodoDto = z.infer<typeof dailyCareShiftFormTodoDtoSchema>;

export const intakeOutputRowDtoSchema = z.object({
  observationCode: z.string().nullable(),
  displayName: z.string().nullable(),
  valueUnit: z.string().nullable(),
  records: z.record(z.string(), intakeOutputCellDtoSchema).nullable(),
  total: z.number().nullable()
});
export type IntakeOutputRowDto = z.infer<typeof intakeOutputRowDtoSchema>;

export const intakeOutputOptionDtoSchema = z.object({
  observationCode: z.string().nullable(),
  displayName: z.string().nullable(),
  valueUnit: z.string().nullable(),
  aspects: z.array(intakeOutputAspectDtoSchema).nullable()
});
export type IntakeOutputOptionDto = z.infer<typeof intakeOutputOptionDtoSchema>;

export const intakeOutputOrderDtoSchema = z.object({
  orderName: z.string().nullable(),
  freqCode: z.string().nullable()
});
export type IntakeOutputOrderDto = z.infer<typeof intakeOutputOrderDtoSchema>;

export const medicationRecordResSchema = z.object({
  administrationId: z.string().nullable(),
  scheduledDatetime: z.string().nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable(),
  effectiveDatetime: z.string().nullable(),
  effectiveUserName: z.string().nullable()
});
export type MedicationRecordRes = z.infer<typeof medicationRecordResSchema>;

export const dualCheckDtoSchema = z.object({
  verifier: z.string().nullable(),
  verifyDatetime: z.string().nullable()
});
export type DualCheckDto = z.infer<typeof dualCheckDtoSchema>;

export const unexecReasonDtoSchema = z.object({
  unexecReasonCode: z.string().nullable(),
  unexecReasonNote: z.string().nullable(),
  unexecReasonUser: z.string().nullable(),
  unexecReasonDatetime: z.string().nullable()
});
export type UnexecReasonDto = z.infer<typeof unexecReasonDtoSchema>;

export const sideEffectDtoSchema = z.object({
  sideEffectCode: z.string().nullable(),
  sideEffectNote: z.string().nullable(),
  sideEffectUser: z.string().nullable(),
  sideEffectDatetime: z.string().nullable()
});
export type SideEffectDto = z.infer<typeof sideEffectDtoSchema>;

export const executionPeriodDtoSchema = z.object({
  startDatetime: z.string().nullable(),
  endDatetime: z.string().nullable()
});
export type ExecutionPeriodDto = z.infer<typeof executionPeriodDtoSchema>;

export const userPatientBedDtoSchema = z.object({
  bedId: z.string().nullable(),
  bedNo: z.string().nullable(),
  patientId: z.string().nullable(),
  encounterId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  diagnosisCode: z.string().nullable(),
  diagnosisName: z.string().nullable(),
  admissionDatetime: z.string().nullable(),
  dischargeDatetime: z.string().nullable(),
  latestSurgeryDatetime: z.string().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  alert: z.array(alertDtoSchema).nullable(),
  transfer: transferDtoSchema.nullable(),
  genderI18n: z.string().nullable()
});
export type UserPatientBedDto = z.infer<typeof userPatientBedDtoSchema>;

export const patientSummaryVitalSignsDtoSchema = z.object({
  painScore: z.number().nullable(),
  bpSys: vitalSignsAlertValueDtoSchema.nullable(),
  bpDia: vitalSignsAlertValueDtoSchema.nullable(),
  bodySiteDto: tenantCodeBaseDtoSchema.nullable(),
  map: z.number().nullable(),
  pulse: vitalSignsAlertValueDtoSchema.nullable(),
  respRate: vitalSignsAlertValueDtoSchema.nullable(),
  bodyTemp: vitalSignsAlertValueDtoSchema.nullable(),
  measurementMethodDto: tenantCodeBaseDtoSchema.nullable(),
  spo2: vitalSignsAlertValueDtoSchema.nullable(),
  obstetricRecords: vitalSignsObstetricRecordDtoSchema.nullable()
});
export type PatientSummaryVitalSignsDto = z.infer<typeof patientSummaryVitalSignsDtoSchema>;

export const patientSummaryMedicationOrderDtoSchema = z.object({
  orderCode: z.string().nullable(),
  orderName: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  orderStartDatetime: z.string().nullable(),
  orderEndDatetime: z.string().nullable(),
  requesterId: z.string().nullable(),
  requesterName: z.string().nullable(),
  stopFlag: z.boolean().nullable(),
  prnFlag: z.boolean().nullable(),
  instructionMemo: z.string().nullable()
});
export type PatientSummaryMedicationOrderDto = z.infer<typeof patientSummaryMedicationOrderDtoSchema>;

export const patientSummaryLastLabReportDtoSchema = z.object({
  id: z.string().nullable(),
  type: z.string().nullable(),
  itemNameEn: z.string().nullable(),
  itemNameZh: z.string().nullable(),
  effectiveDatetime: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  reportName: z.string().nullable(),
  valueQuantity: z.number().nullable(),
  valueUnit: z.string().nullable(),
  valueString: z.string().nullable(),
  resultStatus: z.string().nullable()
});
export type PatientSummaryLastLabReportDto = z.infer<typeof patientSummaryLastLabReportDtoSchema>;

export const patientSummaryShiftHandoverDtoSchema = z.object({
  shiftPeriod: z.string().nullable(),
  recorderId: z.string().nullable(),
  recorderName: z.string().nullable(),
  content: z.string().nullable(),
  recordDatetime: z.string().nullable()
});
export type PatientSummaryShiftHandoverDto = z.infer<typeof patientSummaryShiftHandoverDtoSchema>;

export const saveIntakeOutputResultDtoSchema = z.object({
  observationCode: z.string().nullable(),
  total: z.number().nullable(),
  lockVersion: z.number().nullable()
});
export type SaveIntakeOutputResultDto = z.infer<typeof saveIntakeOutputResultDtoSchema>;

export const dailyCareHomeResSchema = z.object({
  shiftPeriod: z.enum(["NIGHT", "DAY", "EVENING"]).nullable(),
  shiftFormTodos: z.array(dailyCareShiftFormTodoDtoSchema).nullable(),
  patientTodos: z.array(dailyCarePatientTodoDtoSchema).nullable(),
  timeline: z.record(z.string(), z.array(dailyCareTimelineGroupDtoSchema)).nullable(),
  medications: z.array(dailyCareMedicationDtoSchema).nullable(),
  vitalSigns: dailyCareVitalSignsDtoSchema.nullable(),
  taskBoardItems: z.array(dailyCareTaskBoardItemDtoSchema).nullable(),
  intakeOutputSummary: dailyCareIntakeOutputSummaryDtoSchema.nullable()
});
export type DailyCareHomeRes = z.infer<typeof dailyCareHomeResSchema>;

export const queryEncounterCardResSchema = z.object({
  id: z.string().nullable(),
  encounterType: z.string().nullable(),
  visitStartDatetime: z.string().nullable(),
  visitEndDatetime: z.string().nullable(),
  doctorName: z.string().nullable(),
  divName: z.string().nullable(),
  icdCode: z.string().nullable(),
  diagnosisDesc: z.string().nullable(),
  allergenNames: z.array(z.string()).nullable(),
  roomNo: z.string().nullable(),
  bedNo: z.string().nullable(),
  seqNo: z.string().nullable(),
  bodyWeight: z.number().nullable(),
  effectiveDatetime: z.string().nullable(),
  surgeryDatetimeList: z.array(z.string()).nullable()
});
export type QueryEncounterCardRes = z.infer<typeof queryEncounterCardResSchema>;

export const allShiftIntakeOutputResSchema = z.object({
  intake: z.array(intakeOutputRowDtoSchema).nullable(),
  output: z.array(intakeOutputRowDtoSchema).nullable()
});
export type AllShiftIntakeOutputRes = z.infer<typeof allShiftIntakeOutputResSchema>;

export const intakeOutputOptionResSchema = z.object({
  intake: z.array(intakeOutputOptionDtoSchema).nullable(),
  output: z.array(intakeOutputOptionDtoSchema).nullable()
});
export type IntakeOutputOptionRes = z.infer<typeof intakeOutputOptionResSchema>;

export const shiftIntakeOutputResSchema = z.object({
  shiftPeriod: z.enum(["NIGHT", "DAY", "EVENING"]).nullable(),
  batchId: z.string().nullable(),
  order: intakeOutputOrderDtoSchema.nullable(),
  intake: z.array(intakeOutputRowDtoSchema).nullable(),
  output: z.array(intakeOutputRowDtoSchema).nullable()
});
export type ShiftIntakeOutputRes = z.infer<typeof shiftIntakeOutputResSchema>;

export const medicationItemResSchema = z.object({
  encounterOrderId: z.string().nullable(),
  orderName: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  medicationType: z.enum(["GENERAL", "INJECTION", "PRN", "EMERGENCY", "EMERGENCY_INJECTION", "TOPICAL"]).nullable(),
  instructionMemo: z.string().nullable(),
  stopFlag: z.boolean().nullable(),
  mixItemNameList: z.array(z.string()).nullable(),
  medicationRecord: z.array(medicationRecordResSchema).nullable()
});
export type MedicationItemRes = z.infer<typeof medicationItemResSchema>;

export const shiftMedicationResSchema = z.object({
  administrationId: z.string().nullable(),
  encounterOrderId: z.string().nullable(),
  orderName: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  medicationType: z.enum(["GENERAL", "INJECTION", "PRN", "EMERGENCY", "EMERGENCY_INJECTION", "TOPICAL"]).nullable(),
  instructionMemo: z.string().nullable(),
  mixItemNameList: z.array(z.string()).nullable(),
  administrationStatus: z.enum(["SCHEDULED", "DUE_SOON", "IN_PROGRESS", "COMPLETED", "SKIPPED", "NOT_DONE", "CANCELLED"]).nullable(),
  scheduleDatetime: z.string().nullable(),
  requestUser: z.string().nullable(),
  requestDatetime: z.string().nullable(),
  executionPeriod: executionPeriodDtoSchema.nullable(),
  effectiveUser: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  sideEffect: sideEffectDtoSchema.nullable(),
  unexecReason: unexecReasonDtoSchema.nullable(),
  prnReason: z.string().nullable(),
  stopFlag: z.boolean().nullable(),
  dualCheck: dualCheckDtoSchema.nullable(),
  lockVersion: z.number().nullable()
});
export type ShiftMedicationRes = z.infer<typeof shiftMedicationResSchema>;

export const bedByStationResSchema = z.object({
  bedId: z.string().nullable(),
  encounterId: z.string().nullable(),
  bedNo: z.string().nullable(),
  roomNo: z.string().nullable(),
  stationName: z.string().nullable(),
  bedClass: z.string().nullable(),
  bedAddFee: z.number().nullable(),
  mrn: z.string().nullable(),
  gender: z.string().nullable(),
  status: z.enum(["active", "empty", "pending"]).nullable(),
  primaryBedFlag: z.boolean().nullable(),
  genderI18n: z.string().nullable()
});
export type BedByStationRes = z.infer<typeof bedByStationResSchema>;

export const careEntryTargetResSchema = z.object({
  targetModule: z.enum(["DAILY_CARE", "ADMISSION_PREPARATION"]).nullable()
});
export type CareEntryTargetRes = z.infer<typeof careEntryTargetResSchema>;

export const deprecatedEncounterResSchema = z.object({
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  inpStatus: z.enum(["IN", "DISC", "TODAY"]).nullable(),
  bedNo: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  attendingDoctorCode: z.string().nullable(),
  attendingDoctorName: z.string().nullable(),
  divCode: z.string().nullable(),
  divName: z.string().nullable(),
  diagnosisCode: z.string().nullable(),
  diagnosisName: z.string().nullable(),
  admissionDateTime: z.string().nullable(),
  dischargeDateTime: z.string().nullable(),
  alerts: z.array(alertDtoSchema).nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  favoriteFlag: z.boolean().nullable(),
  genderI18n: z.string().nullable(),
  divCodeI18n: z.string().nullable()
});
export type DeprecatedEncounterRes = z.infer<typeof deprecatedEncounterResSchema>;

export const nursePatientResSchema = z.object({
  encounterId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  patientId: z.string().nullable(),
  inpStatus: z.enum(["IN", "DISC", "TODAY"]).nullable(),
  bedNo: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  attendingDoctorCode: z.string().nullable(),
  attendingDoctorName: z.string().nullable(),
  diagnosisCode: z.string().nullable(),
  diagnosisName: z.string().nullable(),
  admissionDateTime: z.string().nullable(),
  dischargeDateTime: z.string().nullable(),
  divCode: z.string().nullable(),
  completeAdmissionFlag: z.boolean().nullable(),
  alerts: z.array(alertDtoSchema).nullable(),
  transfer: transferDtoSchema.nullable(),
  genderI18n: z.string().nullable(),
  divCodeI18n: z.string().nullable()
});
export type NursePatientRes = z.infer<typeof nursePatientResSchema>;

export const encounterResSchema = z.object({
  bedId: z.string().nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  inpStatus: z.enum(["IN", "DISC", "TODAY"]).nullable(),
  bedNo: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  attendingDoctorCode: z.string().nullable(),
  attendingDoctorName: z.string().nullable(),
  divCode: z.string().nullable(),
  divName: z.string().nullable(),
  diagnosisCode: z.string().nullable(),
  diagnosisName: z.string().nullable(),
  admissionDateTime: z.string().nullable(),
  dischargeDateTime: z.string().nullable(),
  alerts: z.array(alertDtoSchema).nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  primaryBedFlag: z.boolean().nullable(),
  transfer: transferDtoSchema.nullable(),
  genderI18n: z.string().nullable(),
  divCodeI18n: z.string().nullable()
});
export type EncounterRes = z.infer<typeof encounterResSchema>;

export const queryUserPatientResSchema = z.object({
  roomNo: z.string().nullable(),
  stationId: z.string().nullable(),
  stationName: z.string().nullable(),
  beds: z.array(userPatientBedDtoSchema).nullable()
});
export type QueryUserPatientRes = z.infer<typeof queryUserPatientResSchema>;

export const patientBasicResSchema = z.object({
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  mrn: z.string().nullable()
});
export type PatientBasicRes = z.infer<typeof patientBasicResSchema>;

export const queryPatientCardResSchema = z.object({
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  icCardRemarkList: z.array(z.string()).nullable(),
  tenantId: z.string().nullable(),
  genderI18n: z.string().nullable()
});
export type QueryPatientCardRes = z.infer<typeof queryPatientCardResSchema>;

export const patientSummaryResSchema = z.object({
  shiftHandover: patientSummaryShiftHandoverDtoSchema.nullable(),
  lastLabReport: z.array(patientSummaryLastLabReportDtoSchema).nullable(),
  medicationOrders: z.array(patientSummaryMedicationOrderDtoSchema).nullable(),
  vitalSigns: patientSummaryVitalSignsDtoSchema.nullable()
});
export type PatientSummaryRes = z.infer<typeof patientSummaryResSchema>;

export const practitionerResSchema = z.object({
  practitionerId: z.string().nullable(),
  practitionerCode: z.string().nullable(),
  practitionerName: z.string().nullable()
});
export type PractitionerRes = z.infer<typeof practitionerResSchema>;

export const queryNurseOptionResSchema = z.object({
  practitionerId: z.string().nullable(),
  practitionerName: z.string().nullable()
});
export type QueryNurseOptionRes = z.infer<typeof queryNurseOptionResSchema>;

export const queryTenantCodeResSchema = z.object({
  id: z.string().nullable(),
  codeType: z.string().nullable(),
  code: z.string().nullable(),
  description: z.string().nullable()
});
export type QueryTenantCodeRes = z.infer<typeof queryTenantCodeResSchema>;

export const myStationResSchema = z.object({
  stationId: z.string().nullable(),
  stationCode: z.string().nullable(),
  stationName: z.string().nullable()
});
export type MyStationRes = z.infer<typeof myStationResSchema>;

export const saveIntakeOutputResSchema = z.object({
  batchId: z.string().nullable(),
  intake: z.array(saveIntakeOutputResultDtoSchema).nullable(),
  output: z.array(saveIntakeOutputResultDtoSchema).nullable()
});
export type SaveIntakeOutputRes = z.infer<typeof saveIntakeOutputResSchema>;

export const saveIntakeOutputItemDtoSchema = z.object({
  observationCode: z.string().min(1),
  valueQuantity: z.number().nullable(),
  aspect: intakeOutputAspectDtoSchema.nullable(),
  note: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type SaveIntakeOutputItemDto = z.infer<typeof saveIntakeOutputItemDtoSchema>;

export const administrationCheckResSchema = z.object({
  dualCheckDatetime: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type AdministrationCheckRes = z.infer<typeof administrationCheckResSchema>;

export const updateMedicationRecordResSchema = z.object({
  administrationId: z.string().nullable(),
  lockVersion: z.number().nullable(),
  executionUser: z.string().nullable()
});
export type UpdateMedicationRecordRes = z.infer<typeof updateMedicationRecordResSchema>;

export const unexecReasonSchema = z.object({
  unexecReasonCode: z.string().nullable(),
  unexecReasonNote: z.string().nullable()
});
export type UnexecReason = z.infer<typeof unexecReasonSchema>;

export const sideEffectSchema = z.object({
  sideEffectCode: z.string().nullable(),
  sideEffectNote: z.string().nullable()
});
export type SideEffect = z.infer<typeof sideEffectSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseDailyCareHomeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: dailyCareHomeResSchema.nullable()
});
export type ApiResponseDailyCareHomeRes = z.infer<typeof apiResponseDailyCareHomeResSchema>;

export const apiResponseQueryEncounterCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryEncounterCardResSchema.nullable()
});
export type ApiResponseQueryEncounterCardRes = z.infer<typeof apiResponseQueryEncounterCardResSchema>;

export const apiResponseAllShiftIntakeOutputResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: allShiftIntakeOutputResSchema.nullable()
});
export type ApiResponseAllShiftIntakeOutputRes = z.infer<typeof apiResponseAllShiftIntakeOutputResSchema>;

export const apiResponseIntakeOutputOptionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: intakeOutputOptionResSchema.nullable()
});
export type ApiResponseIntakeOutputOptionRes = z.infer<typeof apiResponseIntakeOutputOptionResSchema>;

export const apiResponseShiftIntakeOutputResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: shiftIntakeOutputResSchema.nullable()
});
export type ApiResponseShiftIntakeOutputRes = z.infer<typeof apiResponseShiftIntakeOutputResSchema>;

export const apiResponseListMedicationItemResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(medicationItemResSchema).nullable()
});
export type ApiResponseListMedicationItemRes = z.infer<typeof apiResponseListMedicationItemResSchema>;

export const apiResponseListShiftMedicationResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(shiftMedicationResSchema).nullable()
});
export type ApiResponseListShiftMedicationRes = z.infer<typeof apiResponseListShiftMedicationResSchema>;

export const apiResponseListBedByStationResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(bedByStationResSchema).nullable()
});
export type ApiResponseListBedByStationRes = z.infer<typeof apiResponseListBedByStationResSchema>;

export const apiResponseCareEntryTargetResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: careEntryTargetResSchema.nullable()
});
export type ApiResponseCareEntryTargetRes = z.infer<typeof apiResponseCareEntryTargetResSchema>;

export const apiResponseListDeprecatedEncounterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(deprecatedEncounterResSchema).nullable()
});
export type ApiResponseListDeprecatedEncounterRes = z.infer<typeof apiResponseListDeprecatedEncounterResSchema>;

export const apiResponseListNursePatientResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(nursePatientResSchema).nullable()
});
export type ApiResponseListNursePatientRes = z.infer<typeof apiResponseListNursePatientResSchema>;

export const apiResponseListEncounterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(encounterResSchema).nullable()
});
export type ApiResponseListEncounterRes = z.infer<typeof apiResponseListEncounterResSchema>;

export const apiResponseListQueryUserPatientResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryUserPatientResSchema).nullable()
});
export type ApiResponseListQueryUserPatientRes = z.infer<typeof apiResponseListQueryUserPatientResSchema>;

export const apiResponseListPatientBasicResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(patientBasicResSchema).nullable()
});
export type ApiResponseListPatientBasicRes = z.infer<typeof apiResponseListPatientBasicResSchema>;

export const apiResponseQueryPatientCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientCardResSchema.nullable()
});
export type ApiResponseQueryPatientCardRes = z.infer<typeof apiResponseQueryPatientCardResSchema>;

export const apiResponsePatientSummaryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: patientSummaryResSchema.nullable()
});
export type ApiResponsePatientSummaryRes = z.infer<typeof apiResponsePatientSummaryResSchema>;

export const apiResponseListPractitionerResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(practitionerResSchema).nullable()
});
export type ApiResponseListPractitionerRes = z.infer<typeof apiResponseListPractitionerResSchema>;

export const apiResponseListQueryNurseOptionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryNurseOptionResSchema).nullable()
});
export type ApiResponseListQueryNurseOptionRes = z.infer<typeof apiResponseListQueryNurseOptionResSchema>;

export const apiResponseMapStringStringSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.record(z.string(), z.string()).nullable()
});
export type ApiResponseMapStringString = z.infer<typeof apiResponseMapStringStringSchema>;

export const apiResponseListQueryTenantCodeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryTenantCodeResSchema).nullable()
});
export type ApiResponseListQueryTenantCodeRes = z.infer<typeof apiResponseListQueryTenantCodeResSchema>;

export const apiResponseListMyStationResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(myStationResSchema).nullable()
});
export type ApiResponseListMyStationRes = z.infer<typeof apiResponseListMyStationResSchema>;

export const cancelMedicationRecordReqSchema = z.object({
  administrationId: z.string().min(1),
  cancelReason: z.string().min(1),
  lockVersion: z.number()
});
export type CancelMedicationRecordReq = z.infer<typeof cancelMedicationRecordReqSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const apiResponseSaveIntakeOutputResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: saveIntakeOutputResSchema.nullable()
});
export type ApiResponseSaveIntakeOutputRes = z.infer<typeof apiResponseSaveIntakeOutputResSchema>;

export const saveIntakeOutputReqSchema = z.object({
  encounterId: z.string().min(1),
  targetDatetime: z.string().min(1),
  shiftPeriod: z.enum(["NIGHT", "DAY", "EVENING"]),
  batchId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  intake: z.preprocess((val) => val === undefined ? null : val, z.array(saveIntakeOutputItemDtoSchema).nullish()),
  output: z.preprocess((val) => val === undefined ? null : val, z.array(saveIntakeOutputItemDtoSchema).nullish())
});
export type SaveIntakeOutputReq = z.infer<typeof saveIntakeOutputReqSchema>;

export const apiResponseAdministrationCheckResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: administrationCheckResSchema.nullable()
});
export type ApiResponseAdministrationCheckRes = z.infer<typeof apiResponseAdministrationCheckResSchema>;

export const administrationCheckReqSchema = z.object({
  administrationId: z.string().min(1),
  userId: z.string().min(1),
  dualCheckDatetime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  password: z.string().min(1),
  lockVersion: z.number()
});
export type AdministrationCheckReq = z.infer<typeof administrationCheckReqSchema>;

export const apiResponseUpdateMedicationRecordResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateMedicationRecordResSchema.nullable()
});
export type ApiResponseUpdateMedicationRecordRes = z.infer<typeof apiResponseUpdateMedicationRecordResSchema>;

export const updateMedicationRecordReqSchema = z.object({
  administrationId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  encounterId: z.string().min(1),
  encounterOrderId: z.string().min(1),
  prnReason: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  executionPeriod: z.preprocess((val) => val === undefined ? null : val, executionPeriodDtoSchema.nullish()),
  sideEffect: z.preprocess((val) => val === undefined ? null : val, sideEffectSchema.nullish()),
  unexecReason: z.preprocess((val) => val === undefined ? null : val, unexecReasonSchema.nullish()),
  lockVersion: z.preprocess((val) => val === undefined ? null : val, z.number().nullish())
});
export type UpdateMedicationRecordReq = z.infer<typeof updateMedicationRecordReqSchema>;

export const nursePatientSaveReqSchema = z.object({
  encounterId: z.string().min(1),
  favoriteFlag: z.boolean()
});
export type NursePatientSaveReq = z.infer<typeof nursePatientSaveReqSchema>;

export const apiResponseVoidSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseVoid = z.infer<typeof apiResponseVoidSchema>;

export const nursePatientPrimaryBedReqSchema = z.object({
  bedId: z.string().min(1),
  primaryBedFlag: z.boolean()
});
export type NursePatientPrimaryBedReq = z.infer<typeof nursePatientPrimaryBedReqSchema>;

// Query Parameter Schemas

export const queryTenantCodeDataByTypeQuerySchema = z.object({
  type: z.enum(["bodySite", "bodyTemperatureMethod", "bloodTransfusion", "drainageTube", "notActiveReason", "sideEffect", "examOrder", "medicationNotAdministered", "medicationTimeWindow", "urine", "stool", "drainage"])
});
export type QueryTenantCodeDataByTypeQuery = z.infer<typeof queryTenantCodeDataByTypeQuerySchema>;

export const queryNurseOptionQuerySchema = z.object({
  keyword: z.string().nullish()
});
export type QueryNurseOptionQuery = z.infer<typeof queryNurseOptionQuerySchema>;

export const queryDoctorQuerySchema = z.object({
  keyword: z.string().nullish()
});
export type QueryDoctorQuery = z.infer<typeof queryDoctorQuerySchema>;

export const queryByKeywordQuerySchema = z.object({
  keyword: z.string()
});
export type QueryByKeywordQuery = z.infer<typeof queryByKeywordQuerySchema>;

export const queryUserPatientByStationListQuerySchema = z.object({
  stationIds: z.array(z.string()).nullish()
});
export type QueryUserPatientByStationListQuery = z.infer<typeof queryUserPatientByStationListQuerySchema>;

export const queryPatientListByParamsQuerySchema = z.object({
  stationIds: z.array(z.string()),
  doctorId: z.string().nullish(),
  inpStatus: z.array(z.enum(["IN", "DISC", "TODAY"])).nullish(),
  patientId: z.string().nullish(),
  bedNo: z.string().nullish()
});
export type QueryPatientListByParamsQuery = z.infer<typeof queryPatientListByParamsQuerySchema>;

export const queryEncounterDetailByParamsQuerySchema = z.object({
  stationIds: z.array(z.string()),
  doctorId: z.string().nullish(),
  inpStatus: z.array(z.enum(["IN", "DISC", "TODAY"])).nullish(),
  patientId: z.string().nullish(),
  bedNo: z.string().nullish()
});
export type QueryEncounterDetailByParamsQuery = z.infer<typeof queryEncounterDetailByParamsQuerySchema>;

export const queryCareEntryTargetQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryCareEntryTargetQuery = z.infer<typeof queryCareEntryTargetQuerySchema>;

export const queryBedByStationListQuerySchema = z.object({
  stationIds: z.array(z.string())
});
export type QueryBedByStationListQuery = z.infer<typeof queryBedByStationListQuerySchema>;

export const queryShiftMedicationQuerySchema = z.object({
  administrationId: z.string().nullish(),
  prnEncounterOrderId: z.string().nullish()
});
export type QueryShiftMedicationQuery = z.infer<typeof queryShiftMedicationQuerySchema>;

export const queryMedicationQuerySchema = z.object({
  encounterId: z.string(),
  startDatetime: z.string(),
  endDatetime: z.string()
});
export type QueryMedicationQuery = z.infer<typeof queryMedicationQuerySchema>;

export const queryShiftIntakeOutputQuerySchema = z.object({
  encounterId: z.string(),
  batchId: z.string().nullish(),
  targetDatetime: z.string().nullish(),
  shiftPeriod: z.enum(["NIGHT", "DAY", "EVENING"]).nullish()
});
export type QueryShiftIntakeOutputQuery = z.infer<typeof queryShiftIntakeOutputQuerySchema>;

export const queryAllShiftIntakeOutputQuerySchema = z.object({
  encounterId: z.string(),
  targetDatetime: z.string()
});
export type QueryAllShiftIntakeOutputQuery = z.infer<typeof queryAllShiftIntakeOutputQuerySchema>;

export const queryDailyCareHomeQuerySchema = z.object({
  encounterId: z.string(),
  targetDatetime: z.string(),
  layout: z.enum(["SHIFT", "OVERVIEW"]).nullish()
});
export type QueryDailyCareHomeQuery = z.infer<typeof queryDailyCareHomeQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
